import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Autocomplete,
    Box,
    Typography,
    CircularProgress,
    Chip,
    IconButton,
    Tooltip,
    alpha,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    Close,
    Engineering as EngineeringIcon,
    Person as PersonIcon,
    Notes as NotesIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { addNotification } from '../../store/notificationSlice';
import { hostsDataApi } from '../../http/hostsDataApi';

const HostsDataDialog = ({ open, onClose, host, hostType, targetHost = null }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const user = useSelector(state => state.store.user);
    const isDarkMode = useSelector(state => state.settings.darkMode);

    const [noteForEngineer, setNoteForEngineer] = useState('');
    const [noteForGuest, setNoteForGuest] = useState('');
    const [selectedHost, setSelectedHost] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isInitialized, setIsInitialized] = useState(false);
    const [hostsNotesInfo, setHostsNotesInfo] = useState({});

    const primaryColor = isDarkMode ? '#58a6ff' : '#0969da';
    const warningColor = isDarkMode ? '#d29922' : '#bf8700';
    const successColor = isDarkMode ? '#3fb950' : '#2da44e';
    const errorColor = '#d63a23';

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'var(--background-paper)',
            borderRadius: { xs: '4px', md: '6px' },
            fontSize: { xs: '10px', md: '11px' },
            '& fieldset': { borderColor: 'var(--border-default)' },
            '&:hover fieldset': { borderColor: primaryColor },
            '&.Mui-focused fieldset': { borderColor: primaryColor }
        },
        '& .MuiInputBase-input': { 
            color: 'var(--text-primary)',
            fontSize: { xs: '10px', md: '11px' },
            padding: { xs: '5px 8px', md: '6px 10px' },
            '&::placeholder': { color: 'var(--text-muted)', opacity: 1 }
        },
        '& .MuiInputLabel-root': { 
            color: 'var(--text-muted)',
            fontSize: { xs: '10px', md: '11px' },
            '&.Mui-focused': { color: primaryColor }
        },
    };

    const autocompleteSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'var(--background-paper)',
            borderRadius: { xs: '4px', md: '6px' },
            '& fieldset': { borderColor: 'var(--border-default)' },
            '&:hover fieldset': { borderColor: primaryColor },
            '&.Mui-focused fieldset': { borderColor: primaryColor }
        },
        '& .MuiInputLabel-root': { 
            color: 'var(--text-muted)',
            fontSize: { xs: '10px', md: '11px' },
            '&.Mui-focused': { color: primaryColor }
        },
        '& .MuiAutocomplete-input': {
            color: 'var(--text-primary)',
            fontSize: { xs: '10px', md: '11px' }
        },
        '& .MuiAutocomplete-popupIndicator': { color: 'var(--text-muted)' },
        '& .MuiAutocomplete-clearIndicator': { color: 'var(--text-muted)' },
    };

    const availableHosts = useMemo(() => {
        const hosts = [];
        if (hostType === 'crossroad' && host?.child) {
            hosts.push({ name: host.name, host: host.name, isMain: true });
            host.child.forEach(childHost => {
                hosts.push({ name: childHost.name, host: childHost.name, isMain: false });
            });
        } else if (host) {
            hosts.push({ name: host.name, host: host.name, isMain: true });
        }
        return hosts;
    }, [host, hostType]);

    const loadAllHostsNotesInfo = async () => {
        const notesInfo = {};
        const promises = availableHosts.map(async (hostOption) => {
            try {
                const response = await hostsDataApi.getHostDataByHostId(hostOption.host);
                if (response.success && response.data) {
                    notesInfo[hostOption.host] = {
                        hasEngineerNote: !!(response.data.note_for_engineer?.trim()),
                        hasGuestNote: !!(response.data.note_for_guest?.trim())
                    };
                } else {
                    notesInfo[hostOption.host] = { hasEngineerNote: false, hasGuestNote: false };
                }
            } catch (error) {
                if (error.response?.status !== 404) {
                    console.error(`Error loading notes info for host ${hostOption.host}:`, error);
                }
                notesInfo[hostOption.host] = { hasEngineerNote: false, hasGuestNote: false };
            }
        });
        await Promise.all(promises);
        setHostsNotesInfo(notesInfo);
    };

    useEffect(() => {
        if (open && !isInitialized) {
            let initialHost = null;
            if (hostType === 'crossroad' && targetHost) {
                initialHost = availableHosts.find(h =>
                    h.name === targetHost ||
                    h.name.includes(targetHost) ||
                    h.name.split(' / ')[1] === targetHost
                );
            }
            if (!initialHost) initialHost = availableHosts[0];
            
            setSelectedHost(initialHost || null);
            if (initialHost) loadHostData(initialHost.host);
            
            if (availableHosts.length > 1) {
                loadAllHostsNotesInfo();
            }
            
            setError('');
            setIsInitialized(true);
        } else if (!open) {
            setIsInitialized(false);
            setNoteForEngineer('');
            setNoteForGuest('');
            setSelectedHost(null);
            setHostsNotesInfo({});
        }
    }, [open, host, hostType, targetHost, availableHosts, isInitialized]);

    const loadHostData = async (host) => {
        try {
            const response = await hostsDataApi.getHostDataByHostId(host);
            if (response.success && response.data) {
                const engineerNote = response.data.note_for_engineer || '';
                const guestNote = response.data.note_for_guest || '';
                setNoteForEngineer(engineerNote);
                setNoteForGuest(guestNote);
                setHostsNotesInfo(prev => ({
                    ...prev,
                    [host]: {
                        hasEngineerNote: !!(engineerNote.trim()),
                        hasGuestNote: !!(guestNote.trim())
                    }
                }));
            } else {
                setNoteForEngineer('');
                setNoteForGuest('');
                setHostsNotesInfo(prev => ({
                    ...prev,
                    [host]: { hasEngineerNote: false, hasGuestNote: false }
                }));
            }
        } catch (error) {
            if (error.response?.status !== 404) {
                console.error('Error loading host data:', error);
            }
            setNoteForEngineer('');
            setNoteForGuest('');
            setHostsNotesInfo(prev => ({
                ...prev,
                [host]: { hasEngineerNote: false, hasGuestNote: false }
            }));
        }
    };

    const handleHostChange = async (event, newValue) => {
        setSelectedHost(newValue);
        if (newValue) {
            await loadHostData(newValue.host);
        }
    };

    const handleSave = async () => {
        if (!selectedHost) {
            setError('Пожалуйста, выберите хост');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await hostsDataApi.upsertHostData(
                selectedHost.host,
                noteForEngineer.trim() || null,
                noteForGuest.trim() || null
            );

            setHostsNotesInfo(prev => ({
                ...prev,
                [selectedHost.host]: {
                    hasEngineerNote: !!(noteForEngineer.trim()),
                    hasGuestNote: !!(noteForGuest.trim())
                }
            }));

            dispatch(addNotification({
                message: 'Заметки успешно сохранены',
                options: { variant: 'success' }
            }));
            onClose();
        } catch (error) {
            console.error('Error saving host data:', error);
            setError(error.response?.data?.error || error.message || 'Ошибка при сохранении');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setNoteForEngineer('');
        setNoteForGuest('');
        setError('');
        onClose();
    };

    const getDisplayName = (hostOption) => {
        if (!hostOption) return '';
        const parts = hostOption.name.split(' / ');
        return parts[1] || parts[0];
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    backgroundColor: 'var(--background-default)',
                    color: 'var(--text-primary)',
                    backgroundImage: 'none',
                    borderRadius: { xs: 0, md: '12px' },
                    border: { xs: 'none', md: '1px solid var(--border-default)' },
                    boxShadow: { xs: 'none', md: 'var(--shadow-lg)' },
                    margin: { xs: 0, md: '32px' },
                    maxHeight: { xs: '100%', md: 'calc(100% - 64px)' },
                    height: { xs: '100%', md: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{
                p: { xs: '8px 12px', md: '10px 16px' },
                backgroundColor: 'var(--background-default)',
                borderBottom: '1px solid var(--border-default)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                gap: { xs: 0.75, md: 0 }
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 }, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 24, md: 28 },
                        height: { xs: 24, md: 28 },
                        borderRadius: { xs: '4px', md: '6px' },
                        backgroundColor: primaryColor,
                        color: 'white',
                        flexShrink: 0
                    }}>
                        <NotesIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                    </Box>
                    <Typography sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' }, fontWeight: 600, color: 'var(--text-primary)' }}>
                        Заметки
                    </Typography>
                    {selectedHost && (
                        <Chip 
                            label={getDisplayName(selectedHost)}
                            size="small"
                            sx={{
                                height: { xs: 18, md: 20 },
                                fontSize: { xs: '9px', md: '10px' },
                                fontWeight: 500,
                                backgroundColor: alpha(primaryColor, 0.12),
                                color: primaryColor
                            }}
                        />
                    )}
                </Box>
                <IconButton 
                    size="small" 
                    onClick={handleCancel}
                    sx={{ 
                        color: 'var(--text-secondary)',
                        padding: { xs: '4px', md: '8px' }
                    }}
                >
                    <Close sx={{ fontSize: { xs: 18, md: 20 } }} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: { xs: 1, md: 2 }, backgroundColor: 'var(--background-default)' }}>
                {error && (
                    <Box sx={{ 
                        mb: { xs: 1.5, md: 2 }, 
                        p: { xs: 1, md: 1.5 }, 
                        backgroundColor: alpha(errorColor, 0.1),
                        border: `1px solid ${alpha(errorColor, 0.3)}`,
                        borderRadius: { xs: '6px', md: '8px' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: { xs: 0.75, md: 1 }
                    }}>
                        <Typography sx={{ fontSize: { xs: '10px', md: '11px' }, color: errorColor, fontWeight: 500, flex: 1 }}>
                            {error}
                        </Typography>
                        <IconButton size="small" onClick={() => setError('')} sx={{ color: errorColor, p: { xs: '2px', md: 0.25 }, flexShrink: 0 }}>
                            <Close sx={{ fontSize: { xs: 12, md: 14 } }} />
                        </IconButton>
                    </Box>
                )}

                {availableHosts.length > 1 && (
                    <Autocomplete
                        value={selectedHost}
                        onChange={handleHostChange}
                        options={availableHosts}
                        getOptionLabel={(option) => getDisplayName(option)}
                        isOptionEqualToValue={(option, value) => option?.host === value?.host}
                        size="small"
                        disableClearable
                        sx={{ ...autocompleteSx, mb: { xs: 1.5, md: 2 }, width: '100%' }}
                        renderOption={(props, option) => {
                            const { key, ...otherProps } = props;
                            const notesInfo = hostsNotesInfo[option.host] || { hasEngineerNote: false, hasGuestNote: false };
                            const hasAnyNote = notesInfo.hasEngineerNote || notesInfo.hasGuestNote;
                            
                            return (
                                <Box
                                    component="li"
                                    key={key}
                                    {...otherProps}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: { xs: 0.75, md: 1 },
                                        py: { xs: 0.5, md: 0.75 },
                                        px: { xs: 1, md: 1.5 },
                                        fontSize: { xs: '10px', md: '11px' },
                                        color: 'var(--text-primary)',
                                        minHeight: { xs: 32, md: 36 },
                                        '&:hover': { backgroundColor: 'var(--hover-bg)' },
                                        '&.Mui-focused': { backgroundColor: 'var(--hover-bg)' }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, md: 1 }, flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ 
                                            fontSize: { xs: '10px', md: '11px' },
                                            color: option.isMain ? primaryColor : 'var(--text-primary)',
                                            fontWeight: option.isMain ? 600 : 400
                                        }}>
                                            {getDisplayName(option)}
                                        </Typography>
                                        {option.isMain && (
                                            <Chip 
                                                label="main" 
                                                size="small" 
                                                sx={{ 
                                                    height: { xs: 14, md: 16 }, 
                                                    fontSize: { xs: '8px', md: '9px' },
                                                    fontWeight: 600,
                                                    backgroundColor: primaryColor,
                                                    color: '#fff',
                                                    '& .MuiChip-label': { px: { xs: 0.5, md: 0.75 } }
                                                }} 
                                            />
                                        )}
                                    </Box>
                                    {hasAnyNote && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.4, md: 0.5 }, flexShrink: 0 }}>
                                            {notesInfo.hasEngineerNote && (
                                                <Tooltip title="Есть заметка для инженера" arrow>
                                                    <EngineeringIcon sx={{ fontSize: { xs: 12, md: 14 }, color: warningColor }} />
                                                </Tooltip>
                                            )}
                                            {notesInfo.hasGuestNote && (
                                                <Tooltip title="Есть заметка для всех" arrow>
                                                    <PersonIcon sx={{ fontSize: { xs: 12, md: 14 }, color: successColor }} />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Хост"
                                placeholder="Поиск хоста..."
                                sx={inputSx}
                            />
                        )}
                        slotProps={{
                            paper: {
                                sx: {
                                    backgroundColor: 'var(--background-paper)',
                                    border: '1px solid var(--border-default)',
                                    boxShadow: 'var(--shadow-lg)',
                                    borderRadius: { xs: '6px', md: '8px' },
                                    mt: 0.5
                                }
                            }
                        }}
                    />
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
                    {/* Заметка для инженера */}
                    <Box sx={{
                        p: { xs: 1, md: 1.5 },
                        backgroundColor: alpha(warningColor, 0.06),
                        border: `1px solid ${alpha(warningColor, 0.2)}`,
                        borderRadius: { xs: '6px', md: '8px' }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 0.75 }, mb: { xs: 0.75, md: 1 } }}>
                            <EngineeringIcon sx={{ fontSize: { xs: 12, md: 14 }, color: warningColor }} />
                            <Typography sx={{ fontSize: { xs: '10px', md: '11px' }, fontWeight: 600, color: warningColor }}>
                                Для инженера
                            </Typography>
                        </Box>
                        <TextField
                            multiline
                            rows={3}
                            value={noteForEngineer}
                            onChange={(e) => setNoteForEngineer(e.target.value)}
                            fullWidth
                            size="small"
                            placeholder="Заметка видна только инженерам..."
                            sx={{
                                ...inputSx,
                                '& .MuiOutlinedInput-root': {
                                    ...inputSx['& .MuiOutlinedInput-root'],
                                    backgroundColor: 'var(--background-default)'
                                },
                                '& .MuiInputBase-input': {
                                    ...inputSx['& .MuiInputBase-input'],
                                    padding: { xs: '4px 6px', md: '5px 8px' }
                                }
                            }}
                        />
                    </Box>

                    {/* Заметка для всех */}
                    <Box sx={{
                        p: { xs: 1, md: 1.5 },
                        backgroundColor: alpha(successColor, 0.06),
                        border: `1px solid ${alpha(successColor, 0.2)}`,
                        borderRadius: { xs: '6px', md: '8px' }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 0.75 }, mb: { xs: 0.75, md: 1 } }}>
                            <PersonIcon sx={{ fontSize: { xs: 12, md: 14 }, color: successColor }} />
                            <Typography sx={{ fontSize: { xs: '10px', md: '11px' }, fontWeight: 600, color: successColor }}>
                                Для всех
                            </Typography>
                        </Box>
                        <TextField
                            multiline
                            rows={3}
                            value={noteForGuest}
                            onChange={(e) => setNoteForGuest(e.target.value)}
                            fullWidth
                            size="small"
                            placeholder="Заметка видна всем пользователям..."
                            sx={{
                                ...inputSx,
                                '& .MuiOutlinedInput-root': {
                                    ...inputSx['& .MuiOutlinedInput-root'],
                                    backgroundColor: 'var(--background-default)'
                                },
                                '& .MuiInputBase-input': {
                                    ...inputSx['& .MuiInputBase-input'],
                                    padding: { xs: '4px 6px', md: '5px 8px' }
                                }
                            }}
                        />
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{
                p: { xs: '8px 12px', md: '10px 16px' },
                backgroundColor: 'var(--background-default)',
                borderTop: '1px solid var(--border-default)',
                gap: { xs: 0.75, md: 1 },
                flexDirection: { xs: 'row', md: 'row' }
            }}>
                <Button
                    onClick={handleCancel}
                    disabled={loading}
                    size="small"
                    fullWidth={false}
                    sx={{ 
                        color: 'var(--text-secondary)',
                        textTransform: 'none',
                        fontSize: { xs: '10px', md: '11px' },
                        borderRadius: { xs: '4px', md: '6px' },
                        padding: { xs: '3px 10px', md: '4px 12px' },
                        width: { xs: '100%', md: 'auto' },
                        '&:hover': { backgroundColor: 'var(--hover-bg)' }
                    }}
                >
                    Отмена
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading}
                    size="small"
                    fullWidth={false}
                    startIcon={loading ? <CircularProgress size={{ xs: 10, md: 12 }} color="inherit" /> : null}
                    sx={{ 
                        textTransform: 'none',
                        fontSize: { xs: '10px', md: '11px' },
                        borderRadius: { xs: '4px', md: '6px' },
                        minWidth: { xs: 'auto', md: 100 },
                        padding: { xs: '3px 10px', md: '4px 12px' },
                        width: { xs: '100%', md: 'auto' },
                        backgroundColor: primaryColor,
                        boxShadow: 'none',
                        '&:hover': { 
                            backgroundColor: alpha(primaryColor, 0.9),
                            boxShadow: 'none' 
                        }
                    }}
                >
                    {loading ? 'Сохранение' : 'Сохранить'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HostsDataDialog;
